import type { ItemInput, PositionType } from '../../api';
import { parseGermanNumber } from '../../format';

/**
 * Reading a bill of quantities pasted from a spreadsheet.
 *
 * Columns, in this order: OZ, Kurztext, Menge, Einheit, and optionally
 * geschätzter EP and Langtext. A row without quantity is a title. Tabs
 * separate columns (what Excel puts on the clipboard); semicolons work too.
 * A first row that looks like a heading ("OZ", "Pos.") is skipped.
 */
export interface PasteResult {
  rows: ItemInput[];
  skipped: number[];
}

const HEADING = /^(oz|pos\.?|position|ordnungszahl)$/i;

function typeFrom(unit: string | null, text: string): PositionType {
  if (unit && /^psch|pauschal$/i.test(unit)) return 'lump';
  if (/\bbedarf/i.test(text) || /\beventual/i.test(text)) return 'contingency';
  if (/\bwahl|\balternativ/i.test(text)) return 'alternative';
  return 'normal';
}

export function parsePastedBoq(input: string): PasteResult {
  const lines = input.replace(/\r/g, '').split('\n');
  const rows: ItemInput[] = [];
  const skipped: number[] = [];
  lines.forEach((line, index) => {
    if (line.trim() === '') return;
    const cols = (line.includes('\t') ? line.split('\t') : line.split(';')).map((c) => c.trim());
    const [oz = '', shortText = '', qty = '', unit = '', ep = '', longText = ''] = cols;
    if (index === 0 && HEADING.test(oz)) return;
    if (!oz || !shortText) {
      skipped.push(index + 1);
      return;
    }
    const quantity = parseGermanNumber(qty);
    if (quantity === undefined) {
      skipped.push(index + 1);
      return;
    }
    if (quantity === null) {
      rows.push({ kind: 'title', oz, shortText });
      return;
    }
    const estimate = parseGermanNumber(ep);
    rows.push({
      kind: 'position',
      oz,
      shortText,
      quantity,
      unit: unit || null,
      positionType: typeFrom(unit || null, shortText),
      estimateUnitPrice: estimate === undefined ? null : estimate,
      longText: longText || null,
    });
  });
  return { rows, skipped };
}

/**
 * Unit prices pasted for one bidder: either "OZ <Tab> EP" per line, or one EP
 * per line in the order of the positions. Returns OZ → price.
 */
export function parsePastedPrices(input: string, positionOz: readonly string[]): { prices: Map<string, number>; unknown: string[] } {
  const lines = input.replace(/\r/g, '').split('\n').map((l) => l.trim()).filter(Boolean);
  const prices = new Map<string, number>();
  const unknown: string[] = [];
  const known = new Set(positionOz);
  const twoColumns = lines.some((l) => /\t|;/.test(l));
  lines.forEach((line, index) => {
    if (twoColumns) {
      const [oz = '', ...rest] = line.split(/\t|;/).map((c) => c.trim());
      const value = parseGermanNumber(rest.filter(Boolean).pop() ?? '');
      if (!known.has(oz)) {
        if (!HEADING.test(oz)) unknown.push(oz);
        return;
      }
      if (typeof value === 'number') prices.set(oz, value);
    } else {
      const oz = positionOz[index];
      const value = parseGermanNumber(line);
      if (oz && typeof value === 'number') prices.set(oz, value);
    }
  });
  return { prices, unknown };
}
